import { useIntl } from 'react-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector, RootState } from '../../redux/store';
import { RedirectOptions } from '../../types/redirectLinks';
import { fetchFiles as fetchFilesApi } from '../../redux/slice/fileUploadSlice';
import axiosInterceptor from '../../utils/axios/axiosInterceptor';
import {
  buildQueryLogAwsS3CpCommand,
  buildQueryLogTemporaryCredentialShellExports,
  queryLogS3TransferConfigureSnippet,
} from '../../constants/commands';

type TempCredsBody = {
  credentials: {
    access_key_id: string;
    secret_access_key: string;
    session_token: string;
  };
};

export function useQueryLogCliUploadPanel(
  uploaderId: string,
  detailsApi: string
) {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(
    (state) => state.userProject.currentProject.project_id
  );
  const uploader = useAppSelector(
    (state) => state.fileUpload.uploaders[projectId]?.[uploaderId]
  );
  const s3ConfigDocUrl = useAppSelector((state: RootState) => {
    const link = state.commands.redirectLinks.find(
      (l) => l.key === RedirectOptions.AWS_CLI_S3_CONFIG
    );
    return link?.value ?? '';
  });

  const [credsBlock, setCredsBlock] = useState<string | null>(null);
  const [credsLoading, setCredsLoading] = useState(false);
  const [credsError, setCredsError] = useState(false);

  const targetBucket = uploader?.bucket ?? '';
  const targetFolder = uploader?.prefix ?? '';

  const fetchCredentials = useCallback(async () => {
    setCredsLoading(true);
    setCredsError(false);
    try {
      const response = await axiosInterceptor().get<TempCredsBody>(
        '/temporary-credentials'
      );
      setCredsBlock(
        buildQueryLogTemporaryCredentialShellExports(response.data.credentials)
      );
    } catch {
      setCredsError(true);
    } finally {
      setCredsLoading(false);
    }
  }, []);

  useEffect(() => {
    setCredsBlock(null);
    setCredsError(false);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }
    dispatch(
      fetchFilesApi({
        id: uploaderId,
        api: detailsApi,
        projectId,
      })
    );
  }, [dispatch, projectId, uploaderId, detailsApi]);

  useEffect(() => {
    if (!projectId) {
      return;
    }
    void fetchCredentials();
  }, [projectId, fetchCredentials]);

  const awsS3CpCommand = useMemo(() => {
    if (!targetBucket || !targetFolder) {
      return '';
    }
    return buildQueryLogAwsS3CpCommand(targetBucket, targetFolder);
  }, [targetBucket, targetFolder]);

  const toolTipTitle = intl.formatMessage({
    id: 'steps.misc.toolTipTitle',
    defaultMessage: 'Copied to Clipboard',
  });

  const handleCopyUploadCommand = () => {
    if (awsS3CpCommand) {
      void navigator.clipboard.writeText(awsS3CpCommand);
    }
  };

  const handleCopyS3ConfigSnippet = () => {
    void navigator.clipboard.writeText(queryLogS3TransferConfigureSnippet);
  };

  const handleCopyCredentials = () => {
    if (credsBlock) {
      void navigator.clipboard.writeText(credsBlock);
    }
  };

  const filesLoading = uploader === undefined || uploader.loading === true;
  const hasDestination = Boolean(targetBucket && targetFolder);

  return {
    projectId,
    credsBlock,
    credsLoading,
    credsError,
    fetchCredentials,
    s3ConfigDocUrl,
    awsS3CpCommand,
    toolTipTitle,
    handleCopyUploadCommand,
    handleCopyS3ConfigSnippet,
    handleCopyCredentials,
    filesLoading,
    hasDestination,
  };
}
